/** @format */

/**
 * Paper-level selection for a CSV import (QB2-223, DR8).
 *
 * One import creates one Paper. The paper-level facts are chosen here, on the
 * page, NOT read from the CSV: qualification, subject, series (season + year),
 * tier, date and paper code. Every imported question inherits these via the
 * paper it references.
 *
 * The qualification, tier and series dropdowns are populated from the Core API
 * (QB2-223). The parent (`CsvValidation`) loads the Core lists once and passes
 * them in as options; this component stays presentational so it is simple to
 * test. Core is a hard dependency (DR11): when a Core list is empty the
 * dropdown is empty too — there is no enum fallback.
 *
 * Every dropdown option's `value` is the Core `code` and its `label` is the
 * display title. The Paper stores those codes (as a CoreRef) for tier, series
 * season and qualification.
 */

import { type PaperLevelValues } from "./paperLevel";

/** A single dropdown option: the stored `value` and its display `label`. */
export interface PaperLevelOption {
	value: string;
	label: string;
}

interface PaperLevelFormProps {
	values: PaperLevelValues;
	onChange: (values: PaperLevelValues) => void;
	/** Subject options sourced from the Core API; falls back to a free-text input when empty. */
	subjectOptions?: string[];
	/** Qualification options from Core (value = Core code, label = title). */
	qualificationOptions: PaperLevelOption[];
	/** Tier options from Core (value = Core code, label = title). */
	tierOptions: PaperLevelOption[];
	/** Series (season) options from Core (value = Core code, label = title). */
	seriesOptions: PaperLevelOption[];
	isDisabled?: boolean;
	/** The cover scan is filling these fields. They stay visible and cannot be edited. */
	isBusy?: boolean;
}

const labelClass = "block text-sm font-semibold text-slate-700 mb-1";
const fieldClass =
	"w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-(--admin-color-primary-strong) focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100";

export const PaperLevelForm = ({
	values,
	onChange,
	subjectOptions = [],
	qualificationOptions,
	tierOptions,
	seriesOptions,
	isDisabled = false,
	isBusy = false,
}: PaperLevelFormProps) => {
	const set = <K extends keyof PaperLevelValues>(key: K, value: PaperLevelValues[K]) =>
		onChange({ ...values, [key]: value });
	const isLocked = isDisabled || isBusy;

	return (
		<fieldset
			disabled={isLocked}
			aria-busy={isBusy}
			className={`mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-(--admin-shadow-raised) ${isBusy ? "opacity-70" : ""}`}
		>
			<legend className="px-1 text-sm font-bold text-slate-700">Paper details (apply to every question)</legend>
			{/* A grid fieldset does not disable its inputs in Chrome. */}
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				<div>
					<label htmlFor="paper-code" className={labelClass}>
						Paper code
					</label>
					<input
						id="paper-code"
						type="text"
						value={values.paperCode}
						disabled={isLocked}
						onChange={(event) => set("paperCode", event.target.value)}
						className={fieldClass}
					/>
				</div>

				<div>
					<label htmlFor="paper-subject" className={labelClass}>
						Subject
					</label>
					{subjectOptions.length > 0 ? (
						<select
							id="paper-subject"
							value={values.subject}
							disabled={isLocked}
							onChange={(event) => set("subject", event.target.value)}
							className={fieldClass}
						>
							<option value="">Select a subject…</option>
							{subjectOptions.map((option) => (
								<option key={option} value={option}>
									{option}
								</option>
							))}
						</select>
					) : (
						<input
							id="paper-subject"
							type="text"
							value={values.subject}
							disabled={isLocked}
							onChange={(event) => set("subject", event.target.value)}
							className={fieldClass}
						/>
					)}
				</div>

				<div>
					<label htmlFor="paper-qualification" className={labelClass}>
						Qualification
					</label>
					<select
						id="paper-qualification"
						value={values.qualification}
						disabled={isLocked}
						onChange={(event) => set("qualification", event.target.value)}
						className={fieldClass}
					>
						<option value="">Select a qualification…</option>
						{qualificationOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>

				<div>
					<label htmlFor="paper-tier" className={labelClass}>
						Tier
					</label>
					<select
						id="paper-tier"
						value={values.tier}
						disabled={isLocked}
						onChange={(event) => set("tier", event.target.value)}
						className={fieldClass}
					>
						<option value="">No tier</option>
						{tierOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>

				<div>
					<label htmlFor="paper-season" className={labelClass}>
						Series
					</label>
					<select
						id="paper-season"
						value={values.season}
						disabled={isLocked}
						onChange={(event) => set("season", event.target.value)}
						className={fieldClass}
					>
						<option value="">Select a series…</option>
						{seriesOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>

				<div>
					<label htmlFor="paper-year" className={labelClass}>
						Year
					</label>
					<input
						id="paper-year"
						type="number"
						min={2000}
						max={2100}
						value={values.year}
						disabled={isLocked}
						onChange={(event) => set("year", event.target.value)}
						className={fieldClass}
					/>
				</div>

				<div>
					<label htmlFor="paper-date" className={labelClass}>
						Paper date
					</label>
					<input
						id="paper-date"
						type="date"
						value={values.date}
						disabled={isLocked}
						onChange={(event) => set("date", event.target.value)}
						className={fieldClass}
					/>
				</div>
			</div>
		</fieldset>
	);
};
